/**
 * useCampaignTextImport — state machine hook for campaign text import pipeline.
 *
 * States: idle → cataloging → catalog-review → detailing → detail-review
 *         → extracting-relations → relations-review → applying → done | error
 *
 * Orchestrates REST calls to 4 text import endpoints.
 */

import { useState, useCallback, useRef } from 'react';
import type {
  CatalogEntry,
  DetailedEntity,
  CampaignRelation,
  WaveApplyResult,
} from '@roll1/shared';
import { useI18n } from '../i18n';

export type CampaignTextImportStep =
  | 'idle'
  | 'cataloging'
  | 'catalog-review'
  | 'detailing'
  | 'detail-review'
  | 'extracting-relations'
  | 'relations-review'
  | 'applying'
  | 'done'
  | 'error';

export interface UseCampaignTextImportReturn {
  step: CampaignTextImportStep;
  error: string | null;
  isLoading: boolean;

  // Data
  text: string;
  catalog: CatalogEntry[];
  details: DetailedEntity[];
  detailProgress: { current: number; total: number } | null;
  relations: CampaignRelation[];
  waveResults: WaveApplyResult[];

  // Actions
  startCatalog: (text: string) => Promise<void>;
  confirmCatalog: (entries: CatalogEntry[]) => Promise<void>;
  confirmDetails: (details: DetailedEntity[]) => void;
  startRelations: () => Promise<void>;
  confirmRelations: (relations: CampaignRelation[]) => void;
  startApply: () => Promise<void>;
  skipRelations: () => void;
  stop: () => void;
  reset: () => void;
}

export function useCampaignTextImport(sessionId: string): UseCampaignTextImportReturn {
  const { t } = useI18n();
  const [step, setStep] = useState<CampaignTextImportStep>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [text, setText] = useState('');
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [confirmedCatalog, setConfirmedCatalog] = useState<CatalogEntry[]>([]);
  const [details, setDetails] = useState<DetailedEntity[]>([]);
  const [confirmedDetails, setConfirmedDetails] = useState<DetailedEntity[]>([]);
  const [detailProgress, setDetailProgress] = useState<{ current: number; total: number } | null>(null);
  const [relations, setRelations] = useState<CampaignRelation[]>([]);
  const [confirmedRelations, setConfirmedRelations] = useState<CampaignRelation[]>([]);
  const [waveResults, setWaveResults] = useState<WaveApplyResult[]>([]);

  const abortRef = useRef<AbortController | null>(null);

  // ── Wave 1: Catalog ─────────────────────────────────────────

  const startCatalog = useCallback(async (inputText: string) => {
    setText(inputText);
    setStep('cataloging');
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(`/api/sessions/${sessionId}/import/text/catalog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: inputText }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
        throw new Error(err.error ?? t('hookError.catalogFailed'));
      }

      const data = await res.json() as { entries: CatalogEntry[] };

      if (data.entries.length === 0) {
        setError(t('hookError.noEntitiesFound'));
        setStep('error');
        return;
      }

      setCatalog(data.entries);
      setStep('catalog-review');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('hookError.catalogFailed'));
      setStep('error');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, t]);

  // ── Wave 2: Detail ──────────────────────────────────────────

  const confirmCatalog = useCallback(async (entries: CatalogEntry[]) => {
    setConfirmedCatalog(entries);
    setStep('detailing');
    setIsLoading(true);
    setError(null);
    setDetails([]);
    setDetailProgress({ current: 0, total: entries.length });

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`/api/sessions/${sessionId}/import/text/detail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text, entries }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
        throw new Error(err.error ?? t('hookError.detailFailed'));
      }

      // Read SSE stream
      const reader = res.body?.getReader();
      if (!reader) throw new Error(t('hookError.noResponseBody'));

      const decoder = new TextDecoder();
      let buffer = '';
      const collectedDetails: DetailedEntity[] = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6)) as {
                entity?: DetailedEntity;
                index?: number;
                total?: number;
                done?: boolean;
                details?: DetailedEntity[];
                error?: string;
              };

              if (event.error) {
                throw new Error(event.error);
              }

              if (event.entity) {
                collectedDetails.push(event.entity);
                setDetails([...collectedDetails]);
                if (event.total !== undefined) {
                  setDetailProgress({ current: (event.index ?? 0) + 1, total: event.total });
                }
              }

              if (event.done && event.details) {
                setDetails(event.details);
              }
            } catch {
              // Skip malformed SSE events
            }
          }
        }
      }

      setStep('detail-review');
    } catch (e) {
      if ((e as Error).name === 'AbortError') {
        setStep('detail-review');
        return;
      }
      setError(e instanceof Error ? e.message : t('hookError.detailFailed'));
      setStep('error');
    } finally {
      setIsLoading(false);
      abortRef.current = null;
    }
  }, [sessionId, text, t]);

  // ── Wave 3: Relations ───────────────────────────────────────

  const confirmDetails = useCallback((selectedDetails: DetailedEntity[]) => {
    setConfirmedDetails(selectedDetails);
    setStep('extracting-relations');
  }, []);

  const startRelations = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(`/api/sessions/${sessionId}/import/text/relations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ entries: confirmedCatalog, details: confirmedDetails }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
        throw new Error(err.error ?? t('hookError.relationFailed'));
      }

      const data = await res.json() as { relations: CampaignRelation[] };
      setRelations(data.relations);
      setStep('relations-review');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('hookError.relationFailed'));
      setStep('error');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, confirmedCatalog, confirmedDetails, t]);

  // Auto-trigger relation extraction when step changes
  // (handled by the component that renders this hook)

  // ── Apply ───────────────────────────────────────────────────

  const confirmRelations = useCallback((selectedRelations: CampaignRelation[]) => {
    setConfirmedRelations(selectedRelations);
  }, []);

  const skipRelations = useCallback(() => {
    setConfirmedRelations([]);
    setStep('applying');
  }, []);

  const startApply = useCallback(async () => {
    setStep('applying');
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(`/api/sessions/${sessionId}/import/text/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          details: confirmedDetails,
          relations: confirmedRelations,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
        throw new Error(err.error ?? t('hookError.applyFailed'));
      }

      const data = await res.json() as { waveResults: WaveApplyResult[] };
      setWaveResults(data.waveResults);
      setStep('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('hookError.applyFailed'));
      setStep('error');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, confirmedDetails, confirmedRelations, t]);

  // ── Control ─────────────────────────────────────────────────

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsLoading(false);
  }, []);

  const reset = useCallback(() => {
    setStep('idle');
    setError(null);
    setIsLoading(false);
    setText('');
    setCatalog([]);
    setConfirmedCatalog([]);
    setDetails([]);
    setConfirmedDetails([]);
    setDetailProgress(null);
    setRelations([]);
    setConfirmedRelations([]);
    setWaveResults([]);
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  return {
    step,
    error,
    isLoading,
    text,
    catalog,
    details,
    detailProgress,
    relations,
    waveResults,
    startCatalog,
    confirmCatalog,
    confirmDetails,
    startRelations,
    confirmRelations,
    startApply,
    skipRelations,
    stop,
    reset,
  };
}
